import { Quaternion, Vector3 } from "three";
import { Quick3DOrthographicCamera } from "../../quick3d-orthographic-camera";
import { GenericZoomControl } from "./generic-zoom-control";
import { IZoomArguments, IZoomControl } from "./zoom-control-interface";

export interface IOrthographicFixedPointZoomArguments extends IZoomArguments {
  ndcPointX?: number;
  ndcPointY?: number;
}

export class OrthographicFixedPointZoomControl implements IZoomControl {
  private cameraQuaternion: Quaternion;
  private genericZoomArguments: IZoomArguments;
  private genericZoomControl: GenericZoomControl;
  private positionAfterZoom: Vector3;
  private positionBeforeZoom: Vector3;
  private positionDelta: Vector3;

  public constructor() {
    this.cameraQuaternion = new Quaternion();
    this.genericZoomControl = new GenericZoomControl();
    this.genericZoomArguments = { zoomDirection: 1, zoomStep: 0.1 };
    this.positionDelta = new Vector3();
    this.positionBeforeZoom = new Vector3();
    this.positionAfterZoom = new Vector3();
  }

  public zoom(
    camera: Quick3DOrthographicCamera,
    zoomArguments: IOrthographicFixedPointZoomArguments = {}
  ): void {
    const {
      zoomDirection = 1,
      zoomStep = 1,
      ndcPointX = 0,
      ndcPointY = 0,
    } = zoomArguments;

    // get current 3D point under mouse at camera far plane
    this.positionBeforeZoom.set(ndcPointX, ndcPointY, 1).unproject(camera);

    // zoom camera, changing projection matrix
    this.genericZoomArguments.zoomDirection = zoomDirection;
    this.genericZoomArguments.zoomStep = zoomStep;
    this.genericZoomControl.zoom(camera, this.genericZoomArguments);

    // get new 3D position of point under mouse at camera far plane
    this.positionAfterZoom.set(ndcPointX, ndcPointY, 1).unproject(camera);

    // if we zoomed in, new position is closer to camera. if we zoomed out, new position is further away
    this.positionDelta.subVectors(
      this.positionBeforeZoom,
      this.positionAfterZoom
    );

    // put delta into camera local space.
    this.positionDelta.applyQuaternion(
      camera.getWorldQuaternion(this.cameraQuaternion).invert()
    );

    camera.position.add(this.positionDelta);
    camera.updateMatrix();
  }
}
